import { Dispatch, SetStateAction, useCallback, useState } from "react";
import useVehicleLookupMode from "../../hooks/useVehicleLookupMode";
import { rsLookupOrder } from "../../utils/rsLookupOrder";

import { useTranslation } from "react-i18next";

import useRsAutomotive from "../../data/useRsAutomotive";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { ILead } from "../../models/Lead";
import { CategoryWithParts } from "../../models/Part";
import { IVehicle } from "../../models/Vehicle";
import useNotification from "../notifications/useNotifications";

type UseRsCategoryLookupProps = {
  lead: ILead | null;
  vehicle: IVehicle | null;
  categories: CategoryWithParts[];
  setCategories: Dispatch<SetStateAction<CategoryWithParts[]>>;
  onLookupComplete: () => void;
};

type RsProduct = {
  rs_item: string;
  rs_price: string;
};

const normalizeRsKey = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase();

const parseRsPrice = (value: string | null | undefined) =>
  parseFloat((value ?? "").replace("£", ""));

export default function useRsCategoryLookup({
  lead,
  vehicle,
  categories,
  setCategories,
  onLookupComplete,
}: UseRsCategoryLookupProps) {
  const { t } = useTranslation("jobs");
  const { dataByKtype, dataByVrm, dataByVin } = useRsAutomotive();
  const hasTmeFeature = CheckFeatureFlag("TME");
  const { identifierType } = useVehicleLookupMode();
  const { addNotification } = useNotification();
  const [rsLoading, setRsLoading] = useState(false);
  const [rsTotal, setRsTotal] = useState(0);

  const applyRsProductsToCategories = useCallback((data: any) => {
    const products = data?.rs_products ?? {};
    const categoryKeys = new Set(categories.map(c => normalizeRsKey(c.category.rsName)));
    const updatesByRsName = new Map<string, { item: string, price: number, data: any }>();
    let totalValue = 0;

    Object.entries(products).forEach(([key, value]) => {
      const normalizedKey = normalizeRsKey(key);
      const product = value as RsProduct;
      const price = parseRsPrice(product.rs_price);

      if (!categoryKeys.has(normalizedKey) || Number.isNaN(price)) return;

      totalValue += price;
      updatesByRsName.set(normalizedKey, {
        item: product.rs_item,
        price,
        data,
      });
    });

    setRsTotal(totalValue);

    setCategories(prev => {
      return prev.map(cat => {
        const rsInfo = updatesByRsName.get(normalizeRsKey(cat.category.rsName));

        if (rsInfo) {
          return { ...cat, category: { ...cat.category, rsPrice: rsInfo.price, rsItem: rsInfo.item, rsData: rsInfo.data } };
        }

        return cat;
      });
    });
  }, [categories, setCategories]);

  const getDataByKtype = useCallback(async (ktype: number | null | undefined, vrm: string | undefined): Promise<boolean> => {
    if (!ktype || !vrm) return false;

    try {
      const res: any = await dataByKtype.mutateAsync({ ktype: ktype, reg_no: vrm });

      if (res.rs_status !== "success") return false;

      applyRsProductsToCategories(res.rs_data);

      return true;
    } catch (error) {
      console.error("RS KType lookup failed:", error);
      return false;
    }
  }, [applyRsProductsToCategories, dataByKtype]);

  const getDataByVrm = useCallback(async (vrm: string | undefined): Promise<boolean> => {
    if (!vrm) return false;

    try {
      const res: any = await dataByVrm.mutateAsync({ reg_no: vrm });

      if (res.rs_status !== "success") return false;

      applyRsProductsToCategories(res.rs_data);

      return true;
    } catch (error) {
      console.error("RS VRM lookup failed:", error);
      return false;
    }
  }, [applyRsProductsToCategories, dataByVrm]);

  const getDataByVin = useCallback(async (vin: string | undefined): Promise<boolean> => {
    if (!vin) return false;

    try {
      const res: any = await dataByVin.mutateAsync({ vin: vin });

      if (res.rs_status !== "success") return false;

      applyRsProductsToCategories(res.rs_data);

      return true;
    } catch (error) {
      console.error("RS VIN lookup failed:", error);
      return false;
    }
  }, [applyRsProductsToCategories, dataByVin]);

  /**
   * Runs the RS lookups in order until one returns data: VRM first with TME enabled
   * (VIN as its backup, then KType), KType first otherwise.
   * `primaryOnly` stops after the first lookup and leaves the rest to a manual run.
   */
  const handleRsLookup = useCallback(async (primaryOnly: boolean = false) => {
    if (vehicle === null && lead?.vehicle === null) {
      addNotification({ primaryText: t("RS Lookup Failed"), variant: "error", });
      return;
    }

    setRsLoading(true);

    const ktype = vehicle?.kType ?? lead?.vehicle?.kType;
    const vrm = vehicle?.vrm ?? lead?.vehicle?.vrm;
    const vin = vehicle?.combinedVIN ?? lead?.vehicle?.combinedVIN;

    const lookupsByKey = {
      vrm: () => getDataByVrm(vrm),
      vin: () => getDataByVin(vin),
      ktype: () => getDataByKtype(ktype, vrm),
    };
    const [primary, ...backups] = rsLookupOrder({ hasTme: hasTmeFeature, identifierType }).map((key) => lookupsByKey[key]);

    try {
      const primarySuccess = await primary();
      if (primarySuccess === true) {
        addNotification({ primaryText: t("RS Lookup Successful"), variant: "success", });
        onLookupComplete();
        return;
      }

      if (primaryOnly) {
        addNotification({ primaryText: t("Manual RS Lookup Required"), variant: "info", });
        return;
      }

      for (const backup of backups) {
        const backupSuccess = await backup();
        if (backupSuccess === true) {
          addNotification({ primaryText: t("RS Lookup Successful"), variant: "success", });
          onLookupComplete();
          return;
        }
      }

      addNotification({ primaryText: t("RS Lookup Failed"), variant: "error", });
      onLookupComplete();
    } catch (error) {
      console.error("RS lookup failed:", error);
      addNotification({ primaryText: t("RS Lookup Failed"), variant: "error", });
      onLookupComplete();
    } finally {
      setRsLoading(false);
    }
  }, [addNotification, getDataByKtype, getDataByVrm, getDataByVin, hasTmeFeature, identifierType, lead?.vehicle, onLookupComplete, vehicle, t]);

  return {
    handleRsLookup,
    rsLoading,
    rsTotal,
  };
}
